import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  ChevronLeftIcon,
  CloseIcon,
  DownloadIcon,
  EditableTitle,
  getFormattedLongDate,
  IconButtonV2,
  Input,
  LoaderSkeleton,
  MeatballsIcon,
  ModalV2 as Modal,
  OverlayPopover,
  PencilIcon,
  Popover,
  ShareIcon,
  toast,
  TopicLibraryCard,
  TrashIcon,
} from '@utilities';

import NarrativeFlowView from '../../../../components/NarrativeFlowView';
import { downloadPowerPointFile } from '../../../../components/Builder/components/Toolbar/components/Download/components/DownloadPowerPoint';
import {
  deleteNarratives,
  shareNarrativeWithClientGroup,
  shareNarrativeWithUsers,
  updateNarrativeName,
} from '@api/narratives';

import { narrativeStatus, setEndpoint } from '@helpers';

import {
  addFlowToProject,
  deleteProject,
  favoriteProject,
  readFlowState,
  readProject,
  readProjectFlows,
  readProjectList,
  removeFlowFromProject,
  renameProject,
  updateFlowState,
} from '@api/narrative-projects';

import ModalShare from '../../../../components/ModalShare';
import {
  buildNarrativeDownloadableData,
  buildNarrativeDownloadFileName,
} from '../../../../components/NarrativeFlowView/utilities/helpers';
import { DEFAULT_DRAFT_NARRATIVE_NAME } from '../../utilities/constants';

import DeleteProjectModal from './components/DeleteProjectModal';
import NarrativePptExportObserver from './components/NarrativePptExportObserver';
import ProjectItem from './components/ProjectItem';
import RenameProjectModal from './components/RenameProjectModal';
import styles from './_index.module.scss';

const VIEW_MODES = {
  LIST: 'list',
  PROJECT_DETAILS: 'project-details',
};
const PROJECT_RENAME_DEBOUNCE_MS = 600;
const PPT_EXPORT_TIMEOUT_MS = 10000;

const buildDraftNarrativeFromFlowState = ({ chatSessionId, draftFlowState }) => {
  const isFocusedOnRun = draftFlowState?.focusFlowId != null;
  const hasDraftNarrative =
    !isFocusedOnRun &&
    draftFlowState &&
    (Array.isArray(draftFlowState?.topics) ? draftFlowState.topics.length > 0 : false);

  if (!hasDraftNarrative || !chatSessionId) return null;

  return {
    id: `draft-${chatSessionId}`,
    isOwner: true,
    name: draftFlowState?.narrativeName?.trim() || DEFAULT_DRAFT_NARRATIVE_NAME,
    status: 'DRAFT',
  };
};

const NarrativesProjectHistory = ({
  activeDraftFlowState = null,
  activeFlowId = null,
  activeSessionId = null,
  deletedNarrativeIdsBySession = {},
  draftNarrativeNamesBySession = {},
  favoriteStatusBySession = {},
  handleChatSessionReady,
  handleFlowSelected,
  isWindowCollapsed = false,
  narrativeNamesById = {},
  onCloseToolWindow,
  onDraftDeleted,
  onDraftSelected,
  onFavoriteChange,
  onNarrativeDeleted,
  onNarrativeNameChange,
  onProjectNameChange,
  projectNamesBySession = {},
  projectNarrativesBySession = {},
}) => {
  const [defaultProjectsList, setDefaultProjectsList] = useState([]);
  const [quickAccessList, setQuickAccessList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRenameModalActive, setIsRenameModalActive] = useState(false);
  const [isDeleteModalActive, setIsDeleteModalActive] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const [readProjectData, setReadProjectData] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [isLoadingProjectNarratives, setIsLoadingProjectNarratives] = useState(false);
  const [projectNarratives, setProjectNarratives] = useState([]);
  const [projectDraftFlowState, setProjectDraftFlowState] = useState(null);
  const [projectDraftNarrative, setProjectDraftNarrative] = useState(null);
  const [narrativePendingDelete, setNarrativePendingDelete] = useState(null);
  const [narrativePendingRename, setNarrativePendingRename] = useState(null);
  const [renamedNarrativeName, setRenamedNarrativeName] = useState('');
  const [narrativeShareId, setNarrativeShareId] = useState(null);
  const [activeNarrativeActionsId, setActiveNarrativeActionsId] = useState(null);
  const [pendingPptExport, setPendingPptExport] = useState(null);
  const pptExportTimeoutIdRef = useRef(null);
  const [shouldSuppressAutoOpen, setShouldSuppressAutoOpen] = useState(false);
  const [viewMode, setViewMode] = useState(
    activeSessionId ? VIEW_MODES.PROJECT_DETAILS : VIEW_MODES.LIST
  );

  const currentController = useRef(null);
  const readProjectControllerRef = useRef(null);
  const readProjectRequestIdRef = useRef(0);
  const readProjectNarrativesRequestIdRef = useRef(0);
  const renameProjectRequestIdRef = useRef(0);
  const latestRequestedProjectNameRef = useRef('');
  const previousProjectNameRef = useRef('');
  const isExternalProjectNameUpdateRef = useRef(false);
  const narrativeActionButtonRefs = useRef({});

  const readProjects = async (setLoading = false) => {
    if (setLoading) setIsLoading(true);
    if (currentController.current !== null) {
      currentController.current.abort();
    }
    const controller = new AbortController();
    currentController.current = controller;
    try {
      const response = await readProjectList({ signal: currentController.current.signal });
      const nextProjectList = Array.isArray(response?.items)
        ? response.items.map((project) => {
            const syncedName = projectNamesBySession?.[project.chatSessionId];
            return typeof syncedName === 'string' ? { ...project, name: syncedName } : project;
          })
        : [];
      setProjectList(nextProjectList);
      handleFilterProjectLists(nextProjectList);
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error fetching Project History:', error);
    } finally {
      if (setLoading && currentController.current === controller && !controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    readProjects(true);
    return () => {
      if (currentController.current) {
        currentController.current.abort();
      }
      if (readProjectControllerRef.current) {
        readProjectControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const syncedEntries = Object.entries(projectNamesBySession || {});
    if (!syncedEntries.length) return;

    setReadProjectData((previousProjectData) => {
      if (!previousProjectData) return previousProjectData;
      const syncedName = projectNamesBySession?.[previousProjectData.chatSessionId];
      if (typeof syncedName !== 'string' || previousProjectData.name === syncedName) {
        return previousProjectData;
      }
      isExternalProjectNameUpdateRef.current = true;
      return { ...previousProjectData, name: syncedName };
    });

    setActiveProject((previousActiveProject) => {
      if (!previousActiveProject) return previousActiveProject;
      const syncedName = projectNamesBySession?.[previousActiveProject.chatSessionId];
      if (typeof syncedName !== 'string' || previousActiveProject.name === syncedName) {
        return previousActiveProject;
      }
      return { ...previousActiveProject, name: syncedName };
    });

    if (viewMode === VIEW_MODES.PROJECT_DETAILS) return;

    setProjectList((previousProjectList) => {
      let didChange = false;
      const nextProjectList = previousProjectList.map((project) => {
        const syncedName = projectNamesBySession?.[project.chatSessionId];
        if (typeof syncedName !== 'string' || project.name === syncedName) return project;
        didChange = true;
        return { ...project, name: syncedName };
      });

      if (!didChange) return previousProjectList;

      handleFilterProjectLists(nextProjectList);
      return nextProjectList;
    });
  }, [projectNamesBySession, viewMode]);

  useEffect(() => {
    const syncedEntries = Object.entries(favoriteStatusBySession || {});
    if (!syncedEntries.length) return;

    setProjectList((previousProjectList) => {
      let didChange = false;
      const nextProjectList = previousProjectList.map((project) => {
        const syncedFavorite = favoriteStatusBySession?.[project.chatSessionId];
        if (typeof syncedFavorite !== 'boolean' || Boolean(project.isFavorite) === syncedFavorite) {
          return project;
        }
        didChange = true;
        return { ...project, isFavorite: syncedFavorite };
      });

      if (!didChange) return previousProjectList;

      handleFilterProjectLists(nextProjectList);
      return nextProjectList;
    });
  }, [favoriteStatusBySession]);

  const handleFilterProjectLists = (projectList) => {
    const quickAccessProjects = projectList.filter((project) => project.isFavorite);
    const defaultProjects = projectList.filter((project) => !project.isFavorite);
    setQuickAccessList(quickAccessProjects);
    setDefaultProjectsList(defaultProjects);
  };

  const updateProjectNameInLists = ({ name, projectSessionId }) => {
    setProjectList((previousProjectList) => {
      const nextProjectList = previousProjectList.map((project) =>
        project.chatSessionId === projectSessionId ? { ...project, name } : project
      );
      handleFilterProjectLists(nextProjectList);
      return nextProjectList;
    });
  };

  const handleSelectProject = (projectSessionId) => {
    setActiveProject((prevProject) => {
      if (prevProject?.chatSessionId === projectSessionId) return prevProject;
      const project =
        projectList.find((project) => project.chatSessionId === projectSessionId) || null;
      return project;
    });
  };

  const handleDeleteProject = async ({ name, projectSessionId }) => {
    try {
      await deleteProject({ projectId: projectSessionId });
      handleSelectProject(null);
      setReadProjectData(null);
      setProjectNarratives([]);
      setViewMode(VIEW_MODES.LIST);
      await readProjects();
      toast.success(`${name} deleted successfully.`);
    } catch (error) {
      toast.error(`Unable to delete ${name}. Please try again.`);
      console.error('Error deleting project:', error);
    }
  };

  const handleReadProject = async (projectSessionId) => {
    const previousProject = activeProject;
    const requestId = ++readProjectRequestIdRef.current;
    if (readProjectControllerRef.current) {
      readProjectControllerRef.current.abort();
    }
    const controller = new AbortController();
    readProjectControllerRef.current = controller;

    try {
      handleSelectProject(projectSessionId);
      const projectData = await readProject({
        projectId: projectSessionId,
        signal: controller.signal,
      });
      if (requestId !== readProjectRequestIdRef.current) return;
      if (typeof handleChatSessionReady === 'function') {
        handleChatSessionReady(projectSessionId);
      }
      if (activeSessionId !== projectSessionId) {
        handleFlowSelected(null);
      }
      const syncedName = projectNamesBySession?.[projectSessionId];
      const nextProjectData =
        typeof syncedName === 'string' ? { ...projectData, name: syncedName } : projectData;
      setReadProjectData(nextProjectData);
      previousProjectNameRef.current = nextProjectData?.name || '';
      setViewMode(VIEW_MODES.PROJECT_DETAILS);
    } catch (error) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
      if (requestId !== readProjectRequestIdRef.current) return;
      setActiveProject(previousProject);
      setReadProjectData(null);
      setViewMode(VIEW_MODES.LIST);
      setShouldSuppressAutoOpen(true);
      toast.error('Unable to open project. Please try again.');
      console.error('Error reading project:', error);
    } finally {
      if (readProjectControllerRef.current === controller) {
        readProjectControllerRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!activeSessionId || isWindowCollapsed) return;
    if (shouldSuppressAutoOpen) return;
    if (
      viewMode === VIEW_MODES.PROJECT_DETAILS &&
      readProjectData?.chatSessionId === activeSessionId
    ) {
      return;
    }
    handleReadProject(activeSessionId);
  }, [
    activeSessionId,
    isWindowCollapsed,
    readProjectData?.chatSessionId,
    shouldSuppressAutoOpen,
    viewMode,
  ]);

  useEffect(() => {
    setShouldSuppressAutoOpen(false);
  }, [activeSessionId, isWindowCollapsed]);

  useEffect(() => {
    if (viewMode !== VIEW_MODES.PROJECT_DETAILS || !readProjectData) {
      setProjectDraftFlowState(null);
      setProjectDraftNarrative(null);
      setProjectNarratives([]);
      setIsLoadingProjectNarratives(false);
      return;
    }

    const controller = new AbortController();
    const requestId = ++readProjectNarrativesRequestIdRef.current;
    setIsLoadingProjectNarratives(true);
    const shouldReuseActiveWorkspaceState =
      readProjectData.chatSessionId === activeSessionId &&
      (activeFlowId != null || activeDraftFlowState != null);

    Promise.allSettled([
      readProjectFlows({
        chatSessionId: readProjectData.chatSessionId,
        signal: controller.signal,
      }),
      shouldReuseActiveWorkspaceState
        ? Promise.resolve({
            flowId: activeFlowId ?? null,
            flowState: activeDraftFlowState,
          })
        : readFlowState({
            chatSessionId: readProjectData.chatSessionId,
            signal: controller.signal,
          }),
    ])
      .then(([flowsResult, draftResult]) => {
        if (requestId !== readProjectNarrativesRequestIdRef.current) return;
        const apiFlows = flowsResult.status === 'fulfilled' ? flowsResult.value : [];
        const normalizedFlows = apiFlows
          .filter((flow) => flow?.id != null && !flow?.is_deleted)
          .map((flow) => ({
            createdDatetime: flow?.complete_datetime
              ? getFormattedLongDate(new Date(flow.complete_datetime))
              : null,
            creator: flow?.creator || null,
            id: flow?.id,
            isOwner: Boolean(flow?.is_owner),
            name: flow?.name || 'Untitled Narrative',
            status: flow?.status || null,
          }));

        setProjectNarratives(normalizedFlows);
        if (draftResult.status === 'fulfilled') {
          const draftFlowState = draftResult?.value?.flowState || null;
          setProjectDraftFlowState(draftFlowState);
          setProjectDraftNarrative(
            buildDraftNarrativeFromFlowState({
              chatSessionId: readProjectData.chatSessionId,
              draftFlowState,
            })
          );
        } else {
          setProjectDraftFlowState(null);
          setProjectDraftNarrative(null);
        }

        if (
          flowsResult.status === 'rejected' &&
          flowsResult.reason?.name !== 'AbortError' &&
          flowsResult.reason?.name !== 'CanceledError'
        ) {
          toast.error('Unable to load project narratives. Please try again.');
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        if (requestId !== readProjectNarrativesRequestIdRef.current) return;
        console.error('Error reading project narratives:', error);
        toast.error('Unable to load project narratives. Please try again.');
      })
      .finally(() => {
        if (requestId !== readProjectNarrativesRequestIdRef.current) return;
        setIsLoadingProjectNarratives(false);
      });

    return () => {
      readProjectNarrativesRequestIdRef.current += 1;
      controller.abort();
    };
  }, [activeSessionId, readProjectData?.chatSessionId, viewMode]);

  const handleRenameProject = async ({ projectSessionId, renamedProject }) => {
    try {
      await renameProject({ chatSessionId: projectSessionId, name: renamedProject });
      if (typeof onProjectNameChange === 'function') {
        onProjectNameChange(projectSessionId, renamedProject);
      }
      setReadProjectData((previousProjectData) => {
        if (!previousProjectData || previousProjectData.chatSessionId !== projectSessionId) {
          return previousProjectData;
        }
        return { ...previousProjectData, name: renamedProject };
      });
      setActiveProject((previousActiveProject) => {
        if (!previousActiveProject || previousActiveProject.chatSessionId !== projectSessionId) {
          return previousActiveProject;
        }
        return { ...previousActiveProject, name: renamedProject };
      });
      // Apply the known result locally instead of refetching
      updateProjectNameInLists({ name: renamedProject, projectSessionId });
      toast.success(`${renamedProject} renamed successfully.`);
    } catch (error) {
      toast.error('Unable to rename project. Please try again.');
      console.error('Error renaming project:', error);
    }
  };

  const handleToggleFavorite = async ({ isFavorite, name, projectSessionId }) => {
    try {
      await favoriteProject({ chatSessionId: projectSessionId, isFavorite: !isFavorite });
      if (typeof onFavoriteChange === 'function') {
        onFavoriteChange(projectSessionId, !isFavorite);
      }
      // Apply the known result locally instead of refetching: the POST has already
      // committed, and a refetch could reapply a stale value from the render snapshot.
      setProjectList((previousProjectList) => {
        const nextProjectList = previousProjectList.map((project) =>
          project.chatSessionId === projectSessionId
            ? { ...project, isFavorite: !isFavorite }
            : project
        );
        handleFilterProjectLists(nextProjectList);
        return nextProjectList;
      });
      toast.success(`${name} ${!isFavorite ? 'added to' : 'removed from'} favorites.`);
    } catch (error) {
      console.error('Error favoriting project:', error);
      toast.error('Unable to update favorite project. Please try again.');
    }
  };

  const handleBackToProjectList = () => {
    setShouldSuppressAutoOpen(true);
    setReadProjectData(null);
    setProjectDraftNarrative(null);
    setProjectNarratives([]);
    setViewMode(VIEW_MODES.LIST);
  };

  const handleProjectNameChange = (nextName) => {
    isExternalProjectNameUpdateRef.current = false;
    const projectSessionId = readProjectData?.chatSessionId;
    if (typeof onProjectNameChange === 'function' && nextName?.trim()) {
      onProjectNameChange(projectSessionId, nextName);
    }
    setReadProjectData((previousProjectData) => {
      if (!previousProjectData) return previousProjectData;
      return { ...previousProjectData, name: nextName };
    });
  };

  const handleProjectNameBlur = (nextName) => {
    const projectSessionId = readProjectData?.chatSessionId;
    const fallbackName = previousProjectNameRef.current || '';
    const normalizedName = nextName?.trim() || fallbackName;

    if (!projectSessionId || !normalizedName) return;
    if (readProjectData?.name === normalizedName) return;

    setReadProjectData((previousProjectData) => {
      if (!previousProjectData || previousProjectData.chatSessionId !== projectSessionId) {
        return previousProjectData;
      }
      return { ...previousProjectData, name: normalizedName };
    });
    if (typeof onProjectNameChange === 'function') {
      onProjectNameChange(projectSessionId, normalizedName);
    }
  };

  const handleRenameProjectTitle = () => {
    const projectSessionId = readProjectData?.chatSessionId;
    const rawName = readProjectData?.name || '';
    const previousName = previousProjectNameRef.current || '';
    const nextName = rawName.trim() || previousName;
    const requestId = ++renameProjectRequestIdRef.current;

    if (!projectSessionId || !nextName) return;
    if (rawName.trim() !== nextName) {
      setReadProjectData((previousProjectData) => {
        if (!previousProjectData || previousProjectData.chatSessionId !== projectSessionId) {
          return previousProjectData;
        }
        return { ...previousProjectData, name: nextName };
      });
      if (typeof onProjectNameChange === 'function') {
        onProjectNameChange(projectSessionId, nextName);
      }
    }
    if (nextName === previousName) return;
    latestRequestedProjectNameRef.current = nextName;

    renameProject({ chatSessionId: projectSessionId, name: nextName })
      .then(() => {
        if (requestId !== renameProjectRequestIdRef.current) return;
        if (latestRequestedProjectNameRef.current !== nextName) return;
        previousProjectNameRef.current = nextName;
        if (typeof onProjectNameChange === 'function') {
          onProjectNameChange(projectSessionId, nextName);
        }
        updateProjectNameInLists({ name: nextName, projectSessionId });
      })
      .catch((error) => {
        if (requestId !== renameProjectRequestIdRef.current) return;
        if (latestRequestedProjectNameRef.current !== nextName) return;
        console.error('Failed to rename project', error);
        if (typeof onProjectNameChange === 'function') {
          onProjectNameChange(projectSessionId, previousName);
        }
        setReadProjectData((previousProjectData) => {
          if (!previousProjectData || previousProjectData.chatSessionId !== projectSessionId) {
            return previousProjectData;
          }
          return { ...previousProjectData, name: previousName };
        });
        toast.error('Unable to rename project. Please try again.');
      });
  };

  useEffect(() => {
    if (viewMode !== VIEW_MODES.PROJECT_DETAILS) return;
    if (isExternalProjectNameUpdateRef.current) {
      isExternalProjectNameUpdateRef.current = false;
      return;
    }

    const currentName = readProjectData?.name?.trim() || '';
    const previousName = previousProjectNameRef.current?.trim() || '';
    if (!currentName || currentName === previousName) return;

    const timer = window.setTimeout(() => {
      handleRenameProjectTitle();
    }, PROJECT_RENAME_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [readProjectData?.name, viewMode]);

  const renderNarrativeCardMeta = (flow) => {
    const creatorName =
      typeof flow?.creator === 'string' && flow.creator.trim() ? flow.creator : null;
    const leftValue = flow?.isOwner ? 'Created by me' : `Created by ${creatorName || 'others'}`;
    const isFinished =
      flow?.status === narrativeStatus.COMPLETED || flow?.status === narrativeStatus.EXCEPTION;
    const rightValue = isFinished ? (
      flow?.createdDatetime || ''
    ) : (
      <IconButtonV2
        aria-label="Narrative processing"
        className={styles['project-narrative-processing-indicator']}
        data-testid="project-narrative-processing-indicator"
        disabled
        icon={<span />}
        isLoading
        size="small"
        variant="subtle"
      />
    );

    return (
      <div className={styles['project-narrative-card-meta']}>
        <span data-testid="project-narrative-created-by">{leftValue}</span>
        <span data-testid="project-narrative-status">{rightValue}</span>
      </div>
    );
  };

  const displayedProjectNarratives = useMemo(() => {
    const chatSessionId = readProjectData?.chatSessionId;
    const deletedNarrativeIds = new Set(
      (chatSessionId ? deletedNarrativeIdsBySession?.[chatSessionId] || [] : []).map(Number)
    );
    const optimisticNarratives = chatSessionId
      ? projectNarrativesBySession?.[chatSessionId] || []
      : [];
    const activeProjectNarratives = projectNarratives.filter(
      (narrative) => !deletedNarrativeIds.has(Number(narrative?.id))
    );

    const applyNarrativeNameOverrides = (narratives = []) =>
      narratives.map((narrative) => {
        const syncedName = narrativeNamesById?.[narrative?.id];
        if (typeof syncedName !== 'string' || narrative?.name === syncedName) return narrative;
        return { ...narrative, name: syncedName };
      });

    if (!Array.isArray(optimisticNarratives) || optimisticNarratives.length === 0) {
      return applyNarrativeNameOverrides(activeProjectNarratives);
    }

    const optimisticIds = new Set(optimisticNarratives.map((narrative) => Number(narrative?.id)));
    const remainingNarratives = activeProjectNarratives.filter(
      (narrative) => !optimisticIds.has(Number(narrative?.id))
    );

    return applyNarrativeNameOverrides([
      ...optimisticNarratives.filter(
        (narrative) => !deletedNarrativeIds.has(Number(narrative?.id))
      ),
      ...remainingNarratives,
    ]);
  }, [
    deletedNarrativeIdsBySession,
    narrativeNamesById,
    projectNarratives,
    projectNarrativesBySession,
    readProjectData?.chatSessionId,
  ]);

  const handleOpenRenameNarrativeModal = (flow) => {
    if (!flow?.id) return;
    setNarrativePendingRename(flow);
    setRenamedNarrativeName(flow?.name || '');
    setActiveNarrativeActionsId(null);
  };

  const handleRenameNarrative = async () => {
    const narrativeId = narrativePendingRename?.id;
    const nextName = renamedNarrativeName?.trim();
    if (!narrativeId || !nextName) return;

    try {
      await updateNarrativeName({ id: narrativeId, name: nextName });
      setProjectNarratives((previousNarratives) =>
        previousNarratives.map((narrative) =>
          Number(narrative?.id) === Number(narrativeId)
            ? { ...narrative, name: nextName }
            : narrative
        )
      );
      if (typeof onNarrativeNameChange === 'function' && readProjectData?.chatSessionId) {
        onNarrativeNameChange({
          chatSessionId: readProjectData.chatSessionId,
          flowId: narrativeId,
          name: nextName,
        });
      }
      toast.success('Narrative renamed successfully.');
    } catch (error) {
      console.error('Failed to rename narrative', error);
      toast.error('Unable to rename narrative. Please try again.');
    } finally {
      setNarrativePendingRename(null);
      setRenamedNarrativeName('');
    }
  };

  useEffect(() => {
    const projectSessionId = readProjectData?.chatSessionId;
    if (!projectSessionId) return;

    if (projectSessionId === activeSessionId && activeDraftFlowState) {
      setProjectDraftNarrative(
        buildDraftNarrativeFromFlowState({
          chatSessionId: projectSessionId,
          draftFlowState: activeDraftFlowState,
        })
      );
      setProjectDraftFlowState(activeDraftFlowState);
      return;
    }

    const syncedDraftName =
      typeof draftNarrativeNamesBySession?.[projectSessionId] === 'string'
        ? draftNarrativeNamesBySession[projectSessionId]
        : null;
    if (!syncedDraftName) return;

    setProjectDraftFlowState((previousDraftFlowState) => {
      if (!previousDraftFlowState) return previousDraftFlowState;
      if (previousDraftFlowState.narrativeName === syncedDraftName) {
        return previousDraftFlowState;
      }
      return { ...previousDraftFlowState, narrativeName: syncedDraftName };
    });

    setProjectDraftNarrative((previousDraftNarrative) => {
      if (!previousDraftNarrative) return previousDraftNarrative;
      if (previousDraftNarrative.name === syncedDraftName) return previousDraftNarrative;
      return { ...previousDraftNarrative, name: syncedDraftName };
    });
  }, [
    activeDraftFlowState,
    activeSessionId,
    draftNarrativeNamesBySession,
    readProjectData?.chatSessionId,
  ]);

  useEffect(() => {
    if (!pendingPptExport?.flowId) return undefined;

    pptExportTimeoutIdRef.current = window.setTimeout(() => {
      pptExportTimeoutIdRef.current = null;
      setPendingPptExport(null);
      toast.error('Unable to prepare PowerPoint export. Please try again.');
    }, PPT_EXPORT_TIMEOUT_MS);

    return () => {
      window.clearTimeout(pptExportTimeoutIdRef.current);
      pptExportTimeoutIdRef.current = null;
    };
  }, [pendingPptExport]);

  const handlePptExportError = useCallback(() => {
    if (pptExportTimeoutIdRef.current) {
      window.clearTimeout(pptExportTimeoutIdRef.current);
      pptExportTimeoutIdRef.current = null;
    }
    setPendingPptExport(null);
    toast.error('Unable to prepare PowerPoint export. Please try again.');
  }, []);

  const handlePptExportReady = useCallback(async ({ downloadData, flowData, request }) => {
    if (!request?.flowId || String(request.flowId) !== String(flowData?.id)) return;

    if (pptExportTimeoutIdRef.current) {
      window.clearTimeout(pptExportTimeoutIdRef.current);
      pptExportTimeoutIdRef.current = null;
    }

    try {
      const exportData = buildNarrativeDownloadableData({ downloadData, flowData });
      if (!exportData) {
        throw new Error('Missing export data');
      }

      await downloadPowerPointFile({
        customFileName: buildNarrativeDownloadFileName(flowData),
        data: exportData,
      });
    } catch (error) {
      console.error('Failed to export narrative PowerPoint', error);
      toast.error('Unable to download PowerPoint. Please try again.');
    } finally {
      setPendingPptExport(null);
    }
  }, []);

  const renderProjectNarratives = () => {
    if (isLoadingProjectNarratives) {
      return (
        <div className={styles['project-list-loader']}>
          <LoaderSkeleton className={styles['project-loader']} height={42} width={463}>
            <rect height="16" rx="3" ry="3" width="403" x="0" y="6" />
            <rect height="16" rx="3" ry="3" width="325" x="0" y="24" />
          </LoaderSkeleton>
        </div>
      );
    }

    if (displayedProjectNarratives.length === 0) {
      return (
        <div className={styles['project-narratives-empty']} data-testid="project-narratives-empty">
          No narratives yet.
        </div>
      );
    }

    return displayedProjectNarratives.map((flow) => {
      const isFinished =
        flow?.status === narrativeStatus.COMPLETED || flow?.status === narrativeStatus.EXCEPTION;
      const isActiveFlow = Number(flow?.id) === Number(activeFlowId);

      return (
        <TopicLibraryCard
          data-testid={`project-narrative-card-${flow.id}`}
          isActive={isActiveFlow}
          key={flow.id}
          onClick={() => {
            handleFlowSelected(flow.id);
            if (typeof handleChatSessionReady === 'function' && readProjectData?.chatSessionId) {
              handleChatSessionReady(readProjectData.chatSessionId);
            }
          }}
          tabIndex={0}
          title={flow.name}
          titleAccessory={
            <>
              <IconButtonV2
                aria-label="More Actions"
                className={styles['project-item-meatball']}
                data-testid={`project-narrative-more-actions-${flow.id}`}
                disabled={!isFinished}
                icon={<MeatballsIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!isFinished) return;
                  setActiveNarrativeActionsId(flow.id);
                }}
                ref={(element) => {
                  narrativeActionButtonRefs.current[flow.id] = element;
                }}
                size="small"
                variant="subtle"
              />
              {isFinished ? (
                <OverlayPopover
                  hasArrow
                  isActive={activeNarrativeActionsId === flow.id}
                  onHide={() => {
                    setActiveNarrativeActionsId(null);
                  }}
                  target={narrativeActionButtonRefs.current[flow.id]}
                >
                  <Popover.Menu>
                    <div
                      className={styles['project-narrative-menu-section-label']}
                      role="presentation"
                    >
                      Narrative Actions
                    </div>
                    <Popover.Item
                      aria-label="Rename Narrative"
                      data-testid={`project-narrative-rename-${flow.id}`}
                      icon={<PencilIcon />}
                      onClick={(event) => {
                        event?.stopPropagation?.();
                        handleOpenRenameNarrativeModal(flow);
                      }}
                    >
                      <p>Rename</p>
                    </Popover.Item>
                    <Popover.Item
                      aria-label="Share Narrative"
                      data-testid={`project-narrative-share-${flow.id}`}
                      icon={<ShareIcon />}
                      onClick={(event) => {
                        event?.stopPropagation?.();
                        setNarrativeShareId(flow.id);
                        setActiveNarrativeActionsId(null);
                      }}
                    >
                      <p>Share</p>
                    </Popover.Item>
                    <Popover.Item
                      aria-label="Delete Narrative"
                      data-testid={`project-narrative-delete-${flow.id}`}
                      icon={<TrashIcon />}
                      onClick={(event) => {
                        event?.stopPropagation?.();
                        setNarrativePendingDelete(flow);
                        setActiveNarrativeActionsId(null);
                      }}
                    >
                      <p>Delete</p>
                    </Popover.Item>
                    <div
                      className={styles['project-narrative-menu-section-divider']}
                      role="separator"
                    />
                    <div
                      className={styles['project-narrative-menu-section-label']}
                      role="presentation"
                    >
                      Download Options
                    </div>
                    <Popover.Item
                      aria-label="Download Narrative XLSX"
                      data-testid={`project-narrative-download-xlsx-${flow.id}`}
                      icon={<DownloadIcon />}
                      onClick={(event) => {
                        event?.stopPropagation?.();
                        const downloadLink = document.createElement('a');
                        downloadLink.href = setEndpoint(`api/flows/${flow.id}/export`);
                        downloadLink.click();
                        setActiveNarrativeActionsId(null);
                      }}
                    >
                      <p>.XLSX File</p>
                    </Popover.Item>
                    {flow?.status === narrativeStatus.COMPLETED ? (
                      <Popover.Item
                        aria-label="Download Narrative PowerPoint"
                        data-testid={`project-narrative-download-ppt-${flow.id}`}
                        icon={<DownloadIcon />}
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          setPendingPptExport({
                            flowId: flow.id,
                          });
                          setActiveNarrativeActionsId(null);
                        }}
                      >
                        <p>.PPT File</p>
                      </Popover.Item>
                    ) : null}
                  </Popover.Menu>
                </OverlayPopover>
              ) : null}
            </>
          }
          variant="dark"
        >
          {renderNarrativeCardMeta(flow)}
        </TopicLibraryCard>
      );
    });
  };

  const renderDraftNarrative = () => {
    if (!projectDraftNarrative) return null;

    const isActiveDraft =
      Boolean(readProjectData?.chatSessionId) &&
      (activeFlowId === null || activeFlowId === undefined) &&
      activeSessionId === readProjectData?.chatSessionId;

    return (
      <TopicLibraryCard
        className={styles['project-draft-card']}
        data-testid={`project-draft-card-${readProjectData?.chatSessionId}`}
        isActive={isActiveDraft}
        key={projectDraftNarrative.id}
        onClick={() => {
          handleFlowSelected(null);
          if (typeof handleChatSessionReady === 'function' && readProjectData?.chatSessionId) {
            handleChatSessionReady(readProjectData.chatSessionId);
          }
          if (typeof onDraftSelected === 'function' && projectDraftFlowState) {
            onDraftSelected(projectDraftFlowState);
          }
        }}
        title={projectDraftNarrative.name}
        titleAccessory={
          <>
            <IconButtonV2
              aria-label="More Actions"
              className={styles['project-item-meatball']}
              data-testid={`project-draft-more-actions-${readProjectData?.chatSessionId}`}
              icon={<MeatballsIcon />}
              onClick={(event) => {
                event.stopPropagation();
                setActiveNarrativeActionsId(projectDraftNarrative.id);
              }}
              ref={(element) => {
                narrativeActionButtonRefs.current[projectDraftNarrative.id] = element;
              }}
              size="small"
              variant="subtle"
            />
            <OverlayPopover
              hasArrow
              isActive={activeNarrativeActionsId === projectDraftNarrative.id}
              onHide={() => {
                setActiveNarrativeActionsId(null);
              }}
              target={narrativeActionButtonRefs.current[projectDraftNarrative.id]}
            >
              <Popover.Menu>
                <div className={styles['project-narrative-menu-section-label']} role="presentation">
                  Narrative Actions
                </div>
                <Popover.Item
                  aria-label="Delete Draft Narrative"
                  data-testid={`project-draft-delete-${readProjectData?.chatSessionId}`}
                  icon={<TrashIcon />}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    setNarrativePendingDelete({
                      id: projectDraftNarrative.id,
                      isDraft: true,
                      name: projectDraftNarrative.name,
                    });
                    setActiveNarrativeActionsId(null);
                  }}
                >
                  <p>Delete</p>
                </Popover.Item>
              </Popover.Menu>
            </OverlayPopover>
          </>
        }
        variant="dark"
      >
        <div className={styles['project-narrative-card-meta']}>
          <span>Created by me</span>
          <span>DRAFT</span>
        </div>
      </TopicLibraryCard>
    );
  };

  const handleDeleteNarrative = async () => {
    if (!narrativePendingDelete?.id) return;

    let hasDeletedNarrative = false;
    let hasFlowStateCleanupError = false;
    let hasRemovedNarrativeFromProject = false;

    try {
      if (narrativePendingDelete?.isDraft) {
        if (readProjectData?.chatSessionId) {
          await updateFlowState({
            chatSessionId: readProjectData.chatSessionId,
            flowState: {
              editablePromptAnswers: {},
              narrativeName: '',
              topics: [],
            },
          });
        }
        setProjectDraftFlowState(null);
        setProjectDraftNarrative(null);
        if (typeof onDraftDeleted === 'function' && readProjectData?.chatSessionId) {
          onDraftDeleted(readProjectData.chatSessionId);
        }
        toast.success('Draft deleted successfully.');
        return;
      }

      if (readProjectData?.chatSessionId) {
        await removeFlowFromProject({
          flowId: narrativePendingDelete.id,
          projectId: readProjectData.chatSessionId,
        });
        hasRemovedNarrativeFromProject = true;
      }
      await deleteNarratives({ ids: [narrativePendingDelete.id] });
      hasDeletedNarrative = true;
      const deletedFlowId = Number(narrativePendingDelete.id);
      const isDeletingActiveNarrative =
        activeFlowId != null && Number(activeFlowId) === deletedFlowId;
      // focusFlowId may briefly point to the previously selected run. Any non-null
      // value represents run-focused state, so clear it when the active run is deleted.
      const shouldClearRunFlowState =
        isDeletingActiveNarrative && activeDraftFlowState?.focusFlowId != null;

      setProjectNarratives((previousFlows) =>
        previousFlows.filter((flow) => Number(flow.id) !== deletedFlowId)
      );
      if (typeof onNarrativeDeleted === 'function' && readProjectData?.chatSessionId) {
        onNarrativeDeleted({
          chatSessionId: readProjectData.chatSessionId,
          flowId: deletedFlowId,
          shouldClearCanvasState: isDeletingActiveNarrative,
          shouldClearRunFlowState,
        });
      }
      if (shouldClearRunFlowState && readProjectData?.chatSessionId) {
        try {
          await updateFlowState({
            chatSessionId: readProjectData.chatSessionId,
            flowState: {
              editablePromptAnswers: {},
              narrativeName: '',
              topics: [],
            },
          });
        } catch (error) {
          hasFlowStateCleanupError = true;
          console.error('Failed to clear deleted narrative flow state', error);
          toast.error(
            'Narrative deleted, but we could not reset the canvas. Please refresh and try again.'
          );
        }
      }
      if (isDeletingActiveNarrative) {
        handleFlowSelected(null);
      }
      if (!hasFlowStateCleanupError) {
        toast.success('Narrative deleted successfully.');
      }
    } catch (error) {
      if (
        hasRemovedNarrativeFromProject &&
        !hasDeletedNarrative &&
        readProjectData?.chatSessionId
      ) {
        try {
          await addFlowToProject({
            flowId: narrativePendingDelete.id,
            projectId: readProjectData.chatSessionId,
          });
        } catch (restoreError) {
          console.error(
            'Failed to restore narrative to project after deletion failed',
            restoreError
          );
        }
      }
      console.error('Failed to delete narrative from project list', error);
      toast.error('Unable to delete narrative. Please try again.');
    } finally {
      setNarrativePendingDelete(null);
    }
  };

  const renderProjectHistory = (listOption) => {
    if (projectList?.length === 0) {
      return;
    }
    if (!Array.isArray(listOption) || listOption.length === 0) {
      return null;
    }

    return (
      <div className={styles['project-history-list']}>
        {listOption.map((project) => {
          const isActive = activeSessionId
            ? activeSessionId === project.chatSessionId
            : activeProject?.chatSessionId === project.chatSessionId;
          return (
            <ProjectItem
              handleReadProject={handleReadProject}
              handleSelectProject={handleSelectProject}
              handleToggleFavorite={handleToggleFavorite}
              isActive={isActive}
              isFavorite={project.isFavorite}
              key={project.id}
              projectId={project.id}
              projectName={project.name}
              projectSessionId={project.chatSessionId}
              setIsDeleteModalActive={setIsDeleteModalActive}
              setIsRenameModalActive={setIsRenameModalActive}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {viewMode !== VIEW_MODES.PROJECT_DETAILS ? (
        <div className={styles['past-projects-header']} data-testid="past-projects-header">
          <p data-testid="project-history-title">
            {' '}
            {isLoading ? 'Loading Projects...' : 'Browse Past Projects'}{' '}
          </p>
          <IconButtonV2
            aria-label="Close"
            data-testid="project-history-close"
            icon={<CloseIcon />}
            onClick={onCloseToolWindow}
            size="medium"
            variant="default"
          />
        </div>
      ) : null}
      <div className={styles['project-history-container']}>
        {viewMode === VIEW_MODES.PROJECT_DETAILS && readProjectData ? (
          <>
            <div className={styles['project-details-header']} data-testid="project-details-header">
              <div className={styles['project-details-top-row']}>
                <IconButtonV2
                  aria-label="Back to projects"
                  data-testid="project-details-back"
                  icon={<ChevronLeftIcon />}
                  onClick={handleBackToProjectList}
                  tooltip="Back"
                  tooltipProps={{ placement: 'bottom' }}
                  variant="subtle"
                />
                <EditableTitle
                  className={styles['project-details-title']}
                  data-testid="project-details-title-input"
                  onBlur={handleProjectNameBlur}
                  onChange={handleProjectNameChange}
                  value={readProjectData?.name || ''}
                />
                <IconButtonV2
                  aria-label="Close"
                  data-testid="project-details-close"
                  icon={<CloseIcon />}
                  onClick={onCloseToolWindow}
                  size="medium"
                  tooltip="Close"
                  tooltipProps={{ placement: 'left' }}
                  variant="default"
                />
              </div>
            </div>
            <div
              className={styles['project-details-content']}
              data-testid="project-details-content"
            >
              {projectDraftNarrative ? (
                <>
                  <h4 className={styles['project-draft-title']} data-testid="project-draft-title">
                    Drafting a narrative...
                  </h4>
                  <div
                    className={styles['project-narratives-list']}
                    data-testid="project-draft-list"
                  >
                    {renderDraftNarrative()}
                  </div>
                </>
              ) : null}
              <h4 data-testid="project-narratives-title">Project narratives</h4>
              <div
                className={styles['project-narratives-list']}
                data-testid="project-narratives-list"
              >
                {renderProjectNarratives()}
              </div>
            </div>
          </>
        ) : viewMode === VIEW_MODES.PROJECT_DETAILS ? (
          <>
            <div className={styles['project-details-header']} data-testid="project-details-header">
              <div className={styles['project-list-loader']}>
                <LoaderSkeleton className={styles['project-loader']} height={36} width={463}>
                  <rect height="16" rx="3" ry="3" width="260" x="0" y="10" />
                </LoaderSkeleton>
              </div>
            </div>
            <div
              className={styles['project-details-content']}
              data-testid="project-details-content-loading"
            >
              <div className={styles['project-list-loader']}>
                <LoaderSkeleton className={styles['project-loader']} height={120} width={463}>
                  <rect height="16" rx="3" ry="3" width="180" x="0" y="8" />
                  <rect height="16" rx="3" ry="3" width="430" x="0" y="36" />
                  <rect height="16" rx="3" ry="3" width="410" x="0" y="64" />
                  <rect height="16" rx="3" ry="3" width="390" x="0" y="92" />
                </LoaderSkeleton>
              </div>
            </div>
          </>
        ) : isLoading ? (
          <div className={styles['project-list-loader']} data-testid="project-history-loading">
            <LoaderSkeleton className={styles['project-loader']} height={42} width={463}>
              <rect height="16" rx="3" ry="3" width="403" x="0" y="6" />
              <rect height="16" rx="3" ry="3" width="325" x="0" y="24" />
            </LoaderSkeleton>
          </div>
        ) : (
          <>
            {quickAccessList.length > 0 ? (
              <div className={styles['quick-access-container']} data-testid="project-quick-access">
                <h4 data-testid="project-quick-access-title"> Quick Access </h4>
                {renderProjectHistory(quickAccessList)}
              </div>
            ) : null}
            <div
              className={styles['default-project-list-container']}
              data-testid="project-all-other"
            >
              <h4 data-testid="project-all-other-title"> All Other Projects </h4>
              {renderProjectHistory(defaultProjectsList)}
            </div>

            <DeleteProjectModal
              handleDeleteProject={handleDeleteProject}
              isModalActive={isDeleteModalActive}
              narrativeCount={activeProject?.narrativeCount || 0}
              projectName={activeProject?.name}
              projectSessionId={activeProject?.chatSessionId}
              setIsModalActive={setIsDeleteModalActive}
            />

            <RenameProjectModal
              handleRenameProject={handleRenameProject}
              isModalActive={isRenameModalActive}
              projectName={activeProject?.name}
              projectSessionId={activeProject?.chatSessionId}
              setIsModalActive={setIsRenameModalActive}
            />
          </>
        )}
      </div>
      {narrativePendingDelete ? (
        <Modal
          buttons={[
            <Button
              key="cancel-delete-narrative"
              onClick={() => setNarrativePendingDelete(null)}
              text="Cancel"
              variant="secondary"
            />,
            <Button
              key="confirm-delete-narrative"
              onClick={handleDeleteNarrative}
              text="Delete"
              variant="error"
            />,
          ]}
          data-testid="project-narrative-delete-modal"
          isActive
          onClose={() => setNarrativePendingDelete(null)}
          title={narrativePendingDelete?.isDraft ? 'Delete Draft Narrative' : 'Delete Narrative'}
        >
          <p>
            Are you sure you want to delete “{narrativePendingDelete?.name || 'this narrative'}”?
            This action is permanent and cannot be undone.
          </p>
        </Modal>
      ) : null}
      {narrativePendingRename ? (
        <Modal
          buttons={[
            <Button
              key="cancel-rename-narrative"
              onClick={() => {
                setNarrativePendingRename(null);
                setRenamedNarrativeName('');
              }}
              text="Cancel"
              variant="secondary"
            />,
            <Button
              isDisabled={
                !renamedNarrativeName?.trim() ||
                renamedNarrativeName.trim() === (narrativePendingRename?.name || '')
              }
              key="save-rename-narrative"
              onClick={handleRenameNarrative}
              text="Save"
            />,
          ]}
          isActive
          onClose={() => {
            setNarrativePendingRename(null);
            setRenamedNarrativeName('');
          }}
          title="Rename Narrative"
        >
          <Input
            aria-label="Rename Narrative"
            className={styles['rename-narrative-input']}
            id="rename-narrative-input"
            label="Narrative Name"
            onChange={(value) => setRenamedNarrativeName(value)}
            value={renamedNarrativeName}
          />
        </Modal>
      ) : null}
      {narrativeShareId ? (
        <ModalShare
          ids={[narrativeShareId]}
          isNarrative
          isSurvey={false}
          setShowModalShare={(isOpen) => {
            if (!isOpen) setNarrativeShareId(null);
          }}
          shareItemWithClients={shareNarrativeWithClientGroup}
          shareItemWithUsers={shareNarrativeWithUsers}
        />
      ) : null}
      {pendingPptExport?.flowId ? (
        <div
          aria-hidden="true"
          className={styles['project-ppt-export-container']}
          inert=""
          tabIndex={-1}
        >
          <NarrativeFlowView
            flowId={pendingPptExport.flowId}
            hidePageTitle
            hideTitle
            renderAfterContent={(hookData) => (
              <NarrativePptExportObserver
                hookData={hookData}
                onExportError={handlePptExportError}
                onExportReady={handlePptExportReady}
                request={pendingPptExport}
              />
            )}
            useWindowScroll={false}
          />
        </div>
      ) : null}
    </div>
  );
};

NarrativesProjectHistory.propTypes = {
  activeDraftFlowState: PropTypes.object,
  activeFlowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  activeSessionId: PropTypes.string,
  deletedNarrativeIdsBySession: PropTypes.object,
  draftNarrativeNamesBySession: PropTypes.object,
  favoriteStatusBySession: PropTypes.object,
  handleChatSessionReady: PropTypes.func,
  handleFlowSelected: PropTypes.func.isRequired,
  isWindowCollapsed: PropTypes.bool,
  narrativeNamesById: PropTypes.object,
  onCloseToolWindow: PropTypes.func,
  onDraftDeleted: PropTypes.func,
  onDraftSelected: PropTypes.func,
  onFavoriteChange: PropTypes.func,
  onNarrativeDeleted: PropTypes.func,
  onNarrativeNameChange: PropTypes.func,
  onProjectNameChange: PropTypes.func,
  projectNamesBySession: PropTypes.object,
  projectNarrativesBySession: PropTypes.object,
};

export default NarrativesProjectHistory;
